import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import {
    TextField,
    IconButton,
    Box,
    Button,
    Typography,
    TableRow,
    TableCell,
    Autocomplete,
    Chip,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ThemeContext } from 'styled-components';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { AuthContext } from '../../auth/providers/AuthProvider';
import { useQuery, useQueryClient } from 'react-query';
import { listUsersTemplates, getDefaultBillDetails, postDefaultStatement } from '../../../services/template/Templates';
import getLastDefaultStatementParticipantIds from '../utils/getLastDefaultStatementParticipantIds';
import FormatNumber from '../../../utils/formatters/FormatNumber';

/**
 * Inline "Add statement" row for Default Bill: same table form UX as AddStatementRow,
 * with participant multi-select + cost, submits to POST /api/bills/default/statements.
 */
const AddDefaultStatementRow = ({ onSuccess }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const [isAdding, setIsAdding] = useState(false);
    const [cost, setCost] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const hasSetDefaultParticipantsRef = useRef(false);

    const { data: billsData } = useQuery(
        ['templates', 'all'],
        () => listUsersTemplates(csrfToken, null),
        { enabled: !!csrfToken && isAdding, staleTime: 5 * 60 * 1000 }
    );

    const { data: defaultBillData } = useQuery(
        ['defaultBill'],
        () => getDefaultBillDetails(csrfToken),
        { enabled: !!csrfToken && isAdding, staleTime: 60 * 1000 }
    );

    const bills = billsData || [];

    const { uniqueParticipants, currentUserParticipant } = useMemo(() => {
        const byUserId = new Map();
        let current = null;
        bills.forEach((bill) => {
            (bill.participants || []).forEach((p) => {
                const userId = p.user_id || p.id;
                if (!userId) return;
                const sub = (p.user_sub || p.sub || '').toUpperCase();
                const isSelf = authClaims?.user_sub && sub === authClaims.user_sub.toUpperCase();
                if (isSelf) current = { id: userId, email: p.email || 'You', user_sub: p.user_sub };
                byUserId.set(userId, { id: userId, email: p.email || 'Unknown', user_sub: p.user_sub });
            });
        });
        return { uniqueParticipants: Array.from(byUserId.values()), currentUserParticipant: current };
    }, [bills, authClaims?.user_sub]);

    const effectiveSelected = useMemo(() => {
        const hasSelf = currentUserParticipant && selectedParticipants.some((p) => p.id === currentUserParticipant.id);
        if (currentUserParticipant && !hasSelf) return [currentUserParticipant, ...selectedParticipants];
        return selectedParticipants;
    }, [currentUserParticipant, selectedParticipants]);

    // Default participant list to last default statement that the current user created (once per form expand)
    useEffect(() => {
        if (!isAdding) {
            hasSetDefaultParticipantsRef.current = false;
            return;
        }
        if (hasSetDefaultParticipantsRef.current || !uniqueParticipants.length || !currentUserParticipant?.id) return;
        const lastIds = getLastDefaultStatementParticipantIds(defaultBillData, currentUserParticipant.id);
        if (!lastIds.length) return;
        const defaultParticipants = uniqueParticipants.filter((p) => lastIds.includes(p.id));
        if (defaultParticipants.length) {
            setSelectedParticipants(defaultParticipants);
            hasSetDefaultParticipantsRef.current = true;
        }
    }, [isAdding, defaultBillData, uniqueParticipants, currentUserParticipant?.id]);

    const handleStartAdd = () => {
        setIsAdding(true);
        setCost('');
        setSelectedParticipants([]);
        setError(null);
        hasSetDefaultParticipantsRef.current = false;
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setCost('');
        setSelectedParticipants([]);
        setError(null);
        hasSetDefaultParticipantsRef.current = false;
    };

    const handleCostChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) setCost(value);
    };

    const isValidCost = cost !== '' && parseFloat(cost) > 0;
    const canSubmit = isValidCost && effectiveSelected.length >= 1;

    const handleSubmitForm = async (e) => {
        if (e) e.preventDefault();
        if (!canSubmit) return;
        const participantIds = effectiveSelected.map((p) => p.id).filter(Boolean);
        if (participantIds.length === 0) {
            setError('Could not resolve participant IDs.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await postDefaultStatement(csrfToken, {
                participant_user_ids: participantIds,
                cost: parseFloat(cost),
            });
            queryClient.invalidateQueries(['defaultBill']);
            queryClient.invalidateQueries(['templates']);
            setCost('');
            setSelectedParticipants([]);
            setIsAdding(false);
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to create statement.');
        } finally {
            setSubmitting(false);
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            color: theme.colors.text,
            fontSize: '16px',
            '& fieldset': { borderColor: theme.colors.secondary || theme.colors.brandSecondary },
            '&:hover fieldset': { borderColor: theme.colors.text },
            '&.Mui-focused fieldset': { borderColor: theme.colors.text },
        },
        '& .MuiInputLabel-root': {
            color: theme.colors.secondary || theme.colors.brandSecondary,
            fontSize: '16px',
            '&.Mui-focused': { color: theme.colors.text },
        },
        '& .MuiInputBase-input': { fontSize: '16px' },
    };

    return (
        <>
            {isAdding && (
                <>
                    <TableRow>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }} />
                        <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                            <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                                Add Statement
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '56px !important', minWidth: '56px !important', maxWidth: '56px !important', p: 0, flexShrink: 0, flexGrow: 0, borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                <IconButton onClick={handleCancelAdd} size="small" sx={{ color: 'inherit', padding: '4px', '&:hover': { backgroundColor: theme.colors.overlayHover } }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                        <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 2 }}>
                            <Box component="form" onSubmit={handleSubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    multiple
                                    options={uniqueParticipants}
                                    value={effectiveSelected}
                                    onChange={(_, next) => setSelectedParticipants(next)}
                                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.email || 'Unknown')}
                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Who's in this split?"
                                            placeholder="Select people"
                                            sx={textFieldSx}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip key={option.id} label={option.email} {...getTagProps({ index })} size="small" sx={{ backgroundColor: theme.colors?.overlayHover, color: theme.colors?.text }} />
                                        ))
                                    }
                                />
                                <TextField
                                    type="text"
                                    inputProps={{ inputMode: 'decimal' }}
                                    label="Cost"
                                    placeholder="Enter cost amount"
                                    value={cost}
                                    onChange={handleCostChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            if (canSubmit) handleSubmitForm(null);
                                        }
                                        if (e.key === 'Escape') handleCancelAdd();
                                    }}
                                    sx={textFieldSx}
                                />
                                {effectiveSelected.length > 0 && cost && parseFloat(cost) > 0 && (
                                    <Typography variant="body2" sx={{ color: theme.colors.text, opacity: 0.8 }}>
                                        Each: ${FormatNumber(parseFloat(cost) / effectiveSelected.length)}
                                    </Typography>
                                )}
                                {error && (
                                    <Alert severity="error" onClose={() => setError(null)}>
                                        {error}
                                    </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                    <Button
                                        type="button"
                                        onClick={handleCancelAdd}
                                        variant="outlined"
                                        fullWidth
                                        sx={{
                                            border: `1px solid ${theme.colors.border || '#ccc'}`,
                                            backgroundColor: 'transparent',
                                            color: theme.colors.text,
                                            '&:hover': { borderColor: theme.colors.border || '#ccc', backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)' },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        fullWidth
                                        disabled={!canSubmit || submitting}
                                        sx={{
                                            border: `1px solid ${canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc')}`,
                                            backgroundColor: 'transparent',
                                            color: canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : theme.colors.text,
                                            '&:hover': {
                                                borderColor: canSubmit ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary) : (theme.colors.border || '#ccc'),
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        {submitting ? 'Adding…' : 'Add'}
                                    </Button>
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell sx={{ width: '56px !important', minWidth: '56px !important', maxWidth: '56px !important', p: 0, flexShrink: 0, flexGrow: 0 }} />
                    </TableRow>
                </>
            )}
            {!isAdding && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleStartAdd}
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{
                                borderColor: theme.current === 'light' ? theme.colors.brandSecondary : (theme.colors.secondary || theme.colors.brandSecondary),
                                color: theme.current === 'light' ? '#000000' : (theme.colors.secondary || theme.colors.brandSecondary),
                                backgroundColor: theme.current === 'light' ? theme.colors.brandSecondary : (theme.colors.tableBackground || theme.colors.background),
                                '&:hover': {
                                    borderColor: theme.current === 'light' ? theme.colors.brandSecondary : (theme.colors.secondary || theme.colors.brandSecondary),
                                    backgroundColor: theme.current === 'light' ? theme.colors.brandSecondary : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                },
                            }}
                        >
                            Add statement
                        </Button>
                    </TableCell>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }} />
                </TableRow>
            )}
        </>
    );
};

export default AddDefaultStatementRow;
